/**
 * `/link/<token>` — adding a second device by link.
 *
 * Platform sync (iCloud Keychain, Google Password Manager) and WebAuthn's own
 * QR hybrid transport already cover most second devices with no code at all.
 * This is the cross-ecosystem case: a 10-minute single-use token that
 * authorises registering one more passkey for an account that already exists.
 */
import { useEffect, useState } from 'react'
import { peekLink, redeemLink, type LinkPeek } from './passkey'
import type { Account } from './session'

export function LinkRedeem({
  token,
  onDone,
}: {
  token: string
  onDone: (account: Account) => void
}): JSX.Element {
  const [peek, setPeek] = useState<LinkPeek | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    void peekLink(token)
      .then(setPeek)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'This link has expired'))
  }, [token])

  const go = async () => {
    setBusy(true)
    setError(null)
    try {
      const result = await redeemLink(token)
      onDone(result.user)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That did not work')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="page">
      <div className="wordmark">
        cards{' '}
        <span className="suit">
          <span className="s-s">♠</span>
          <span className="s-h">♥</span>
          <span className="s-d">♦</span>
          <span className="s-c">♣</span>
        </span>
      </div>
      <section className="live" style={{ marginTop: 'auto', marginBottom: 'auto' }}>
        <h2>{peek ? `Add this device to ${peek.displayName}` : 'Checking the link'}</h2>
        <p>
          {error ??
            'You will be asked for a fingerprint, face or PIN. That creates a second passkey for the same account — the first one keeps working.'}
        </p>
        <button className="btn btn-primary btn-block" disabled={!peek || busy} onClick={() => void go()}>
          {busy ? 'Waiting for your device…' : 'Add this device'}
        </button>
      </section>
    </div>
  )
}
